import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { BrainCircuit, Mail, Lock, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSound } from '../../hooks/useSound';

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { playHover, playClick } = useSound();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    playClick();

    try {
      await signIn(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err) {
      setError('Invalid email or password');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 px-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ...springConfig }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={springConfig}
              className="relative inline-block"
            >
              <BrainCircuit className="w-16 h-16 text-amber-400" />
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 bg-amber-400/10 rounded-full filter blur-2xl"
              />
            </motion.div>
          </Link>
          <h1 className="text-3xl font-bold mt-4 mb-2">Welcome Back</h1>
          <p className="text-gray-400">Sign in to access your dashboard</p>
        </div>

        <motion.form
          onSubmit={handleSubmit}
          className="space-y-6 p-8 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-center"
            >
              {error}
            </motion.div>
          )}

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-300">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
                placeholder="john@company.com"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-gray-300">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                required
                className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
                placeholder="••••••••"
              />
            </div>
          </div>

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
            onMouseEnter={playHover}
            className="w-full px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </motion.button>

          <div className="text-center text-gray-400">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-amber-400 hover:text-amber-300 transition-colors duration-300"
            >
              Sign Up
            </Link>
          </div>
        </motion.form>
      </motion.div>
    </div>
  );
};